// DEPLOY: /var/www/woody.wtf/frontend/src/pages/Announcements.js
// Announcements feed: /announcements
// The newest announcements across every company (ASX and SEC), with the
// price-sensitive / material marker, AI rating and summary. The ticker opens
// the company page; the headline opens the document. Filters: company
// (ticker search, as Chart.js - /chart/search), market (the exchange),
// source (ASX / EDGAR), price-sensitive only, AI-rated only. While showing the newest page it
// re-fetches every minute, matching the ASX poll, so new announcements and
// summaries appear without reloading. Below MOBILE_BP (768px, as
// Company.js / Workspace.js) the table becomes cards.
// Admins get a "Push to Discord" button under each AI summary (DiscordPush).
// Summaries written with the company's history also carry a novelty tag
// (NoveltyTag, beside the dots) and a "compared with before" line under the summary.
// Then the expected move and surprise (ImpactTags), with the expectation the surprise
// was judged against (surprise_basis) as an "Expected:" line under "compared with before".
// Thematics (ThematicTags, after the novelty tag) are the labels from the admin
// Thematics prompt that the AI matched to the announcement.
// Filters also take thematic (any, or one label - the list comes from
// /company/thematics, so new labels appear by themselves) and significance
// (tick any of 1-5; none ticked = all). Both run server-side, so paging works.
// Route is wrapped in ProtectedRoute in App.js; the API is @login_required.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import DiscordPush from '../components/DiscordPush';
import NoveltyTag from '../components/NoveltyTag';
import ImpactTags from '../components/ImpactTags';
import ReactionTag from '../components/ReactionTag';
import ThematicTags from '../components/ThematicTags';
import AnalysisToggle from '../components/AnalysisToggle';
import { useAuth } from '../context/AuthContext';
import api from '../api';

const PAGE_SIZE  = 100;
const MOBILE_BP  = 768;
const REFRESH_MS = 60 * 1000;
const SEARCH_DEBOUNCE = 300;                            // as Chart.js
const SOURCE_LABELS = { asx: 'ASX', sec: 'EDGAR', bw: 'Business Wire', gnw: 'GlobeNewswire', prn: 'PR Newswire', anw: 'ACCESS Newswire', nfc: 'Newsfile', dod: 'Dept of War', insider: 'Insider buying' };   // market.announcements.source → shown name
// A release can arrive from several sources (newswires, then the SEC exhibit):
// the API returns one row with the copies in `also`, shown as extra sources/links;
// the source list is in order of appearance (release time).
const label   = s => SOURCE_LABELS[s] || s;
const sources = a => [a, ...(a.also || [])].sort((x, y) => new Date(x.announced_at) - new Date(y.announced_at)).map(c => label(c.source)).join(' · ');

const STYLES = `
  /* Fixed layout: the column widths below hold whatever the cells contain (long names,
     thematic tags, an expanded analysis); the summary column takes the rest. */
  .an-table { width: 100%; border-collapse: collapse; font-size: 13px; table-layout: fixed; }
  .an-table td { overflow-wrap: anywhere; }
  .an-table th {
    position: sticky; top: 0; z-index: 1; background: var(--surface);
    font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: 0.1em;
    text-transform: uppercase; color: var(--text-muted);
    text-align: left; padding: 10px 12px; border-bottom: 1px solid var(--border); white-space: nowrap;
  }
  .an-table td { padding: 10px 12px; border-bottom: 1px solid var(--border); vertical-align: top; }
  /* Row tint from the AI direction; unanalysed rows stay white. Hover darkens the tint a shade. */
  .an-table tbody tr.dir-positive, .an-card.dir-positive { background: var(--pos-faint); }
  .an-table tbody tr.dir-negative, .an-card.dir-negative { background: var(--neg-faint); }
  .an-table tbody tr.dir-mixed,    .an-card.dir-mixed    { background: var(--mix-faint); }
  .an-table tbody tr:hover              { background: var(--bg); }
  .an-table tbody tr.dir-positive:hover { background: var(--pos-hover); }
  .an-table tbody tr.dir-negative:hover { background: var(--neg-hover); }
  .an-table tbody tr.dir-mixed:hover    { background: var(--mix-hover); }
  .an-small { font-size: 11px; color: var(--text-muted); white-space: nowrap; }
  .an-ps    { text-align: center; color: var(--accent); }
  .an-tick  { font-family: 'JetBrains Mono', monospace; font-weight: 600; color: var(--accent); text-decoration: none; }
  .an-tick:hover { text-decoration: underline; }
  .an-name  { display: block; font-size: 11px; color: var(--text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .an-link  { color: var(--text); text-decoration: none; }
  .an-link:hover { color: var(--accent); text-decoration: underline; }
  .an-badge { display: inline-block; font-family: 'JetBrains Mono', monospace; font-size: 10px; padding: 1px 6px;
              border-radius: var(--radius); border: 1px solid var(--border); color: var(--text-muted); margin-right: 6px; white-space: nowrap; }
  /* Importance dots and the key above the table */
  .sig-dots { display: inline-flex; gap: 2px; vertical-align: middle; white-space: nowrap; }
  .sig-dots i { display: inline-block; width: 7px; height: 7px; border-radius: 50%; border: 1px solid currentColor; opacity: 0.35; }
  .sig-dots i.on { background: currentColor; opacity: 1; }
  .sig-key { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 18px; font-size: 11px; color: var(--text-muted); margin-bottom: 8px; }
  .sig-key > span { display: inline-flex; align-items: center; gap: 6px; }
  .sig-key .sw { display: inline-block; width: 12px; height: 12px; border-radius: 3px; border: 1px solid var(--border); }
  .sig-key .sw:not(:first-child) { margin-left: 10px; }
  .an-sum   { color: var(--text-muted); cursor: pointer; }
  .an-sum.clamped { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .an-vs    { margin-top: 4px; font-size: 12px; color: var(--text-muted); cursor: pointer; }
  .an-vs.clamped { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
  .an-aimodel { display: block; margin-top: 2px; font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--text-muted); }
  .an-filter { display: flex; flex-wrap: wrap; gap: 8px 16px; align-items: center; margin-bottom: 16px; font-size: 13px; }
  .an-filter select { font-family: 'Space Grotesk', sans-serif; font-size: 12px; padding: 4px 8px;
                      border: 1px solid var(--border); border-radius: var(--radius); background: var(--surface); color: var(--text); }
  .an-card { padding: 12px 14px; border-bottom: 1px solid var(--border); font-size: 13px; }
  .an-card:last-child { border-bottom: none; }
  .an-card .top { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-bottom: 6px; }
  /* Ticker search - same look as the Chart.js search box. */
  .an-search { position: relative; flex: 0 0 240px; }
  .an-search input { width: 100%; padding: 5px 28px 5px 10px; font-size: 12px; font-family: 'JetBrains Mono', monospace; letter-spacing: 0.05em;
                     border: 1px solid var(--border); border-radius: var(--radius); background: var(--surface); color: var(--text); outline: none; }
  .an-search input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-dim); }
  .an-search .clear { position: absolute; right: 6px; top: 50%; transform: translateY(-50%); border: none; background: none;
                      cursor: pointer; color: var(--text-muted); font-size: 13px; line-height: 1; padding: 2px 4px; }
  .an-search .clear:hover { color: var(--accent); }
  .an-drop { position: absolute; top: calc(100% + 4px); left: 0; width: 340px; max-width: 90vw; background: var(--surface); border: 1px solid var(--border);
             border-radius: var(--radius); box-shadow: var(--shadow); z-index: 100; max-height: 280px; overflow-y: auto; }
  .an-drop div { padding: 8px 12px; cursor: pointer; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid var(--border); }
  .an-drop div:hover { background: var(--bg); }
  .an-drop .sym  { font-family: 'JetBrains Mono', monospace; font-weight: 500; flex-shrink: 0; }
  .an-drop .nm   { font-size: 12px; color: var(--text-muted); flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .an-drop .bdg  { font-size: 10px; font-family: 'JetBrains Mono', monospace; padding: 2px 6px; border-radius: 3px; font-weight: 600; flex-shrink: 0; }
  .an-drop .bdg.AU  { background: var(--info-dim); color: var(--info); }
  .an-drop .bdg.US  { background: var(--warning-dim); color: var(--warning); }
  .an-drop .bdg.etf { background: var(--accent-dim); color: var(--accent); }
`;

const S = {
  page: { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1400px', margin: '0 auto', padding: '32px' },
  title: { fontSize: '20px', fontWeight: 600, letterSpacing: '-0.01em', marginBottom: '16px' },
  wrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', overflow: 'auto', maxHeight: 'calc(100vh - 220px)' },
  state: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' },
  error: { background: 'var(--surface)', border: '1px solid var(--error)', borderRadius: 'calc(var(--radius) * 2)', padding: '16px', color: 'var(--error)', fontSize: '13px' },
  more: { marginTop: '12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text)', fontSize: '12px', padding: '6px 14px', cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif" },
};

// Browser-local date and time, e.g. "22 Sep 2026, 08:35".
const when = iso => new Date(iso).toLocaleString('en-GB',
  { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const keyOf = a => `${a.source}-${a.id}-${a.instrument_id}`;
const dirClass = a => ['positive', 'negative'].includes(a.direction) ? `dir-${a.direction}`
                   : ['mixed', 'neutral'].includes(a.direction) ? 'dir-mixed' : '';

// Importance: how much the announcement changes the company's value and prospects, 1-5 (the significance column)
// (the AI's score), as filled dots. Direction is shown by the row colour.
function SigDots({ n }) {
  return (
    <span className="sig-dots" style={{ color: 'var(--accent)' }}
          title={`Importance ${n}/5 - how much this changes the company's value and prospects`}>
      {[1, 2, 3, 4, 5].map(i => <i key={i} className={i <= n ? 'on' : ''} />)}
    </span>
  );
}

// Key for the row colours, shown above the announcements table.
const SigKey = () => (
  <div className="sig-key">
    <span><i className="sw" style={{ background: 'var(--pos-faint)' }} />positive
          <i className="sw" style={{ background: 'var(--neg-faint)' }} />negative
          <i className="sw" style={{ background: 'var(--mix-faint)' }} />mixed / neutral</span>
  </div>
);

export default function AnnouncementsPage() {
  const { user } = useAuth();
  const [market, setMarket]       = useState('');      // '' | AU | US - the exchange
  const [source, setSource]       = useState('');      // '' | asx | sec
  const [ticker, setTicker]       = useState('');      // company filter, e.g. BHP:AU
  const [inputVal, setInputVal]   = useState('');
  const [results, setResults]     = useState([]);
  const [showDrop, setShowDrop]   = useState(false);
  const searchTimer = useRef(null);
  const abortRef    = useRef(null);
  const [sensitive, setSensitive] = useState(false);
  const [rated, setRated]         = useState(false);
  const [thematic, setThematic]   = useState('');      // '' | 'any' | a label
  const [sigs, setSigs]           = useState([]);      // ticked significance ratings; [] = all
  const [labels, setLabels]       = useState([]);      // thematic labels for the dropdown
  const [rows, setRows]           = useState([]);
  const [hasMore, setHasMore]     = useState(false);
  const [paged, setPaged]         = useState(false);   // true once older pages are loaded
  const [open, setOpen]           = useState({});      // expanded summaries
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [isMobile, setIsMobile]   = useState(window.innerWidth < MOBILE_BP);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BP);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Load a page: `last` null = newest page (replaces the list), otherwise the
  // oldest row shown - its time, id and instrument together are the cursor.
  const load = useCallback((last) => {
    setError(null);
    const q = new URLSearchParams({ limit: PAGE_SIZE });
    if (market) q.set('market', market);
    if (source) q.set('source', source);
    if (ticker) q.set('ticker', ticker);
    if (sensitive) q.set('sensitive', '1');
    if (rated) q.set('rated', '1');
    if (thematic) q.set('thematic', thematic);
    if (sigs.length) q.set('sig', sigs.join(','));
    if (last) {
      q.set('before', last.announced_at);
      q.set('before_id', last.id);
      q.set('before_iid', last.instrument_id);
    }
    return api.get(`/company/feed?${q}`)
      .then(({ data }) => {
        setRows(prev => last ? [...prev, ...data.announcements] : data.announcements);
        setHasMore(data.has_more);
        if (last) setPaged(true);
      })
      .catch(err => setError(err?.response?.data?.error || 'Failed to load announcements.'))
      .finally(() => setLoading(false));
  }, [market, source, ticker, sensitive, rated, thematic, sigs]);

  // Thematic labels for the filter, once.
  useEffect(() => {
    api.get('/company/thematics').then(({ data }) => setLabels(data.labels)).catch(() => {});
  }, []);
  const toggleSig = n => setSigs(xs => xs.includes(n) ? xs.filter(x => x !== n) : [...xs, n].sort());

  // Filters changed → start again from the newest page.
  useEffect(() => { setLoading(true); setPaged(false); load(null); }, [load]);

  // Refresh the newest page every minute - but not once older pages are
  // loaded, so the list never jumps while scrolling back through it.
  useEffect(() => {
    if (paged) return;
    const t = setInterval(() => load(null), REFRESH_MS);
    return () => clearInterval(t);
  }, [paged, load]);

  // Ticker search, as Chart.js: debounced /chart/search, pick from the dropdown.
  const doSearch = q => {
    if (abortRef.current) abortRef.current.abort();
    if (!q) { setResults([]); setShowDrop(false); return; }
    abortRef.current = new AbortController();
    api.get(`/chart/search?q=${encodeURIComponent(q)}`, { signal: abortRef.current.signal })
      .then(r => { setResults(r.data); setShowDrop(r.data.length > 0); })
      .catch(() => {});
  };
  const onSearchChange = e => {
    setInputVal(e.target.value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => doSearch(e.target.value.trim()), SEARCH_DEBOUNCE);
  };
  const pick = r => {
    const t = `${r.symbol}:${r.exchange}`;
    if (searchTimer.current) clearTimeout(searchTimer.current);   // no late search reopening the list
    setInputVal(t); setTicker(t); setShowDrop(false);
  };
  const onSearchKey = e => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const v = inputVal.trim().toUpperCase();
    if (results.length) pick(results[0]);                       // top suggestion
    else if (v.includes(':')) { setInputVal(v); setTicker(v); setShowDrop(false); }  // typed in full
  };
  const clearTicker = () => { setTicker(''); setInputVal(''); setResults([]); setShowDrop(false); };

  const company = a => (
    <>
      <Link className="an-tick" to={`/company?ticker=${encodeURIComponent(a.ticker)}`}>{a.ticker}</Link>
      <span className="an-name">{a.name}</span>
    </>
  );

  const headline = a => (
    <>
      {a.source === 'sec' && a.category && (
        <span className="an-badge">{a.category}{a.subcategory ? ` · ${a.subcategory}` : ''}</span>
      )}
      {a.url
        ? <a className="an-link" href={a.url} target="_blank" rel="noopener noreferrer">{a.headline}</a>
        : a.headline}
      {(a.also || []).filter(c => c.url).map(c => (
        <a key={c.source + c.url} className="an-badge" style={{ marginLeft: '6px', textDecoration: 'none' }}
           href={c.url} target="_blank" rel="noopener noreferrer" title="The same release from another source">{label(c.source)}</a>
      ))}
    </>
  );

  const rating = a => a.significance != null
    ? <><SigDots n={a.significance} /><NoveltyTag n={a.novelty} />
        <ReactionTag model={a.model} /><ImpactTags move={a.expected_move} surprise={a.surprise} basis={a.surprise_basis} positioning={a.positioning} positioningBasis={a.positioning_basis} financing={a.financing_risk} /><ThematicTags t={a.thematics} /></>
    : null;

  // Summary clamped to two lines (and "compared with before" to one); click to expand.
  // Admins also see which AI setup wrote it (the API leaves ai_model out for everyone else),
  // and a button to push the announcement to Discord.
  const summary = a => {
    const k = keyOf(a), toggle = () => setOpen(o => ({ ...o, [k]: !o[k] }));
    return a.summary ? (
      <>
        <div className={`an-sum${open[k] ? '' : ' clamped'}`} onClick={toggle}
             title={open[k] ? 'Show less' : 'Show more'}>
          {a.summary}
        </div>
        {a.vs_before && (
          <div className={`an-vs${open[k] ? '' : ' clamped'}`} onClick={toggle}
               title="Compared with the company's earlier announcements">↔ {a.vs_before}</div>
        )}
        {a.surprise_basis && (
          <div className={`an-vs${open[k] ? '' : ' clamped'}`} onClick={toggle}
               title="What was expected before this release">Expected: {a.surprise_basis}</div>
        )}
        <AnalysisToggle text={a.analysis} />
        {a.ai_model && <span className="an-aimodel">AI: {a.ai_model}</span>}
        {user?.is_admin && <DiscordPush source={a.source} id={a.id} />}
      </>
    ) : <span className="an-small">—</span>;
  };

  const marker = a => a.price_sensitive ? '●' : a.material ? '○' : '';
  const markerTitle = a => a.price_sensitive ? 'Price sensitive (ASX)' : a.material ? 'Material by filing type' : undefined;

  return (
    <div style={S.page}>
      <style>{STYLES}</style>
      <Navbar />
      <div style={{ ...S.content, ...(isMobile && { padding: '16px 12px' }) }}>
        <div style={S.title}>Announcements</div>

        <div className="an-filter">
          <div className="an-search">
            <input value={inputVal} onChange={onSearchChange} onKeyDown={onSearchKey}
                   onFocus={() => results.length > 0 && !ticker && setShowDrop(true)}
                   onBlur={() => setTimeout(() => setShowDrop(false), 150)}
                   placeholder="Company, e.g. BHP:AU" spellCheck={false} autoCapitalize="characters" />
            {(ticker || inputVal) && <button className="clear" onClick={clearTicker} title="Show all companies">✕</button>}
            {showDrop && (
              <div className="an-drop">
                {results.map(r => (
                  <div key={`${r.symbol}:${r.exchange}`} onMouseDown={() => pick(r)}>
                    <span className="sym">{r.symbol}</span>
                    <span className="nm">{r.name}</span>
                    <span className={`bdg ${r.exchange}`}>{r.exchange}</span>
                    {['ETF', 'etf'].includes(r.asset_type) && <span className="bdg etf">ETF</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <select value={market} onChange={e => setMarket(e.target.value)}>
            <option value="">All markets</option>
            <option value="AU">AU</option>
            <option value="US">US</option>
          </select>
          <select value={source} onChange={e => setSource(e.target.value)}>
            <option value="">All sources</option>
            {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label><input type="checkbox" checked={sensitive} onChange={e => setSensitive(e.target.checked)} /> Price-sensitive only</label>
          <label><input type="checkbox" checked={rated} onChange={e => setRated(e.target.checked)} /> AI-rated only</label>
          <select value={thematic} onChange={e => setThematic(e.target.value)}>
            <option value="">All thematics</option>
            <option value="any">Any thematic</option>
            {labels.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}
                title="Only these importance ratings - none ticked shows all">
            Importance
            {[1, 2, 3, 4, 5].map(n => (
              <label key={n}><input type="checkbox" checked={sigs.includes(n)} onChange={() => toggleSig(n)} /> {n}</label>
            ))}
          </span>
        </div>

        {error ? <div style={S.error}>{error}</div>
        : loading ? <div style={S.state}>Loading announcements…</div>
        : rows.length === 0 ? <div style={S.state}>No announcements match these filters.</div>
        : (
          <>
            <SigKey />
            <div style={{ ...S.wrap, ...(isMobile && { maxHeight: 'none' }) }}>
              {isMobile ? rows.map(a => (
                <div className={`an-card ${dirClass(a)}`} key={keyOf(a)}>
                  <div className="top">
                    <span className="an-small">{when(a.announced_at)} · {sources(a)}</span>
                    {marker(a) && <span style={{ color: 'var(--accent)' }} title={markerTitle(a)}>{marker(a)}</span>}
                    {rating(a)}
                    <Link className="an-tick" to={`/company?ticker=${encodeURIComponent(a.ticker)}`}>{a.ticker}</Link>
                  </div>
                  <div>{headline(a)}</div>
                  {a.summary && <div style={{ marginTop: '6px' }}>{summary(a)}</div>}
                </div>
              )) : (
                <table className="an-table">
                  <colgroup>
                    <col style={{ width: '112px' }} /><col style={{ width: '76px' }} /><col style={{ width: '48px' }} />
                    <col style={{ width: '160px' }} /><col style={{ width: '26%' }} /><col style={{ width: '150px' }} /><col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Released</th>
                      <th>Source</th>
                      <th title="● price sensitive (ASX)  ○ material by filing type (SEC)">Sens.</th>
                      <th>Company</th>
                      <th>Announcement</th>
                      <th>Importance</th>
                      <th>AI summary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(a => (
                      <tr key={keyOf(a)} className={dirClass(a)}>
                        <td className="an-small">{when(a.announced_at)}</td>
                        <td className="an-small">{sources(a)}</td>
                        <td className="an-ps" title={markerTitle(a)}>{marker(a)}</td>
                        <td style={{ overflow: 'hidden' }} title={a.name}>{company(a)}</td>
                        <td>{headline(a)}</td>
                        <td>{rating(a)}</td>
                        <td>{summary(a)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {hasMore && (
              <button style={S.more} onClick={() => load(rows[rows.length - 1])}>Load older</button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
